import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTicket, faPaperPlane, faExclamationTriangle,
    faInfoCircle, faQuestionCircle, faBug, faWrench
} from '@fortawesome/free-solid-svg-icons';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import localForage from 'localforage';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, useBaseUrl } from '../../../utils/utils';

interface TicketForm {
    subject: string;
    description: string;
    priority: 'low' | 'medium' | 'high' | 'urgent';
    category: 'technical' | 'billing' | 'general' | 'feature-request' | 'bug-report';
}

const CreateTicket: React.FC = () => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const [formData, setFormData] = useState<TicketForm>({
        subject: '',
        description: '',
        priority: 'medium',
        category: 'general',
    });
    const [loading, setLoading] = useState(false);
    const [clinicName, setClinicName] = useState<string>('Facility');
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [staffId, setStaffId] = useState<number | null>(null);

    useEffect(() => {
        document.title = "Create Support Ticket - Uzima Healthcare";
        loadClinicData();
    }, []);

    const loadClinicData = async () => {
        try {
            const storedClinicName = await localForage.getItem('clinic_name');
            if (storedClinicName) {
                setClinicName(storedClinicName as string);
            }
            const cid = await localForage.getItem('clinic_id');
            if (cid) setClinicId(Number(cid));
            const sid = await localForage.getItem('staff_id');
            if (sid) setStaffId(Number(sid));
        } catch (error) {
            console.error('Error loading clinic data:', error);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const getPriorityIcon = (priority: string) => {
        switch (priority) {
            case 'urgent': return faExclamationTriangle;
            case 'high': return faExclamationTriangle;
            case 'medium': return faInfoCircle;
            case 'low': return faInfoCircle;
            default: return faInfoCircle;
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'urgent': return 'danger';
            case 'high': return 'warning';
            case 'medium': return 'info';
            case 'low': return 'success';
            default: return 'info';
        }
    };

    const getCategoryIcon = (category: string) => {
        switch (category) {
            case 'technical': return faWrench;
            case 'billing': return faInfoCircle;
            case 'general': return faQuestionCircle;
            case 'feature-request': return faInfoCircle;
            case 'bug-report': return faBug;
            default: return faQuestionCircle;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Validate form
            if (!formData.subject.trim() || !formData.description.trim()) {
                Swal.fire({
                    icon: 'error',
                    title: 'Validation Error',
                    text: 'Please fill in all required fields'
                });
                setLoading(false);
                return;
            }

            const res = await fetch(buildApiUrl(baseUrl, `facilities/platform-tickets/`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    clinic_id: clinicId, staff_id: staffId,
                    subject: formData.subject, description: formData.description,
                    category: formData.category, priority: formData.priority,
                }),
            });
            const data = await res.json();
            if (!res.ok || data.status !== 'success') {
                throw new Error(data.message || 'Failed to create ticket');
            }

            Swal.fire({
                icon: 'success',
                title: 'Ticket Created!',
                text: 'Your support ticket has been submitted successfully.',
                timer: 2500,
                showConfirmButton: false
            });

            setFormData({ subject: '', description: '', priority: 'medium', category: 'general' });
            navigate('/institution/ticket-list');

        } catch (error) {
            console.error('Error creating ticket:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to create ticket. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="vertical-overlay" />
            
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Header */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <h2 className="fw-bold mb-1">
                                    <FontAwesomeIcon icon={faTicket} className="me-2 text-primary" />
                                    Create Support Ticket
                                </h2>
                                <p className="text-muted mb-0">
                                    Submit a support request for {clinicName}
                                </p>
                            </div>
                        </div>

                        {/* Form */}
                        <div className="row">
                            <div className="col-lg-8">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white border-0">
                                        <h5 className="card-title mb-0">Ticket Details</h5>
                                    </div>
                                    <div className="card-body">
                                        <form onSubmit={handleSubmit}>
                                            {/* Subject */}
                                            <div className="mb-4">
                                                <label className="form-label">Subject *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="subject"
                                                    value={formData.subject}
                                                    onChange={handleInputChange}
                                                    placeholder="Brief description of your issue"
                                                    required
                                                />
                                            </div>

                                            {/* Category and Priority */}
                                            <div className="row mb-4">
                                                <div className="col-md-6">
                                                    <label className="form-label">Category *</label>
                                                    <select
                                                        className="form-select"
                                                        name="category"
                                                        value={formData.category}
                                                        onChange={handleInputChange}
                                                        required
                                                    >
                                                        <option value="general">General Inquiry</option>
                                                        <option value="technical">Technical Issue</option>
                                                        <option value="billing">Billing Question</option>
                                                        <option value="feature-request">Feature Request</option>
                                                        <option value="bug-report">Bug Report</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">Priority *</label>
                                                    <select
                                                        className="form-select"
                                                        name="priority"
                                                        value={formData.priority}
                                                        onChange={handleInputChange}
                                                        required
                                                    >
                                                        <option value="low">Low</option>
                                                        <option value="medium">Medium</option>
                                                        <option value="high">High</option>
                                                        <option value="urgent">Urgent</option>
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Description */}
                                            <div className="mb-4">
                                                <label className="form-label">Description *</label>
                                                <textarea
                                                    className="form-control"
                                                    name="description"
                                                    value={formData.description}
                                                    onChange={handleInputChange}
                                                    rows={8}
                                                    placeholder="Please provide detailed information about your issue, including steps to reproduce if applicable..."
                                                    required
                                                />
                                            </div>

                                            {/* Attachments */}
                                            {/* Submit Button */}
                                            <div className="d-flex justify-content-end">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Creating Ticket...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                                                            Create Ticket
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>

                            {/* Sidebar */}
                            <div className="col-lg-4">
                                {/* Ticket Preview */}
                                <div className="card border-0 shadow-sm mb-4">
                                    <div className="card-header bg-white border-0">
                                        <h6 className="card-title mb-0">Ticket Preview</h6>
                                    </div>
                                    <div className="card-body">
                                        {formData.subject ? (
                                            <div>
                                                <h6 className="mb-2">{formData.subject}</h6>
                                                <div className="d-flex align-items-center gap-3 mb-2">
                                                    <span className={`badge bg-${getPriorityColor(formData.priority)}`}>
                                                        <FontAwesomeIcon icon={getPriorityIcon(formData.priority)} className="me-1" />
                                                        {formData.priority.toUpperCase()}
                                                    </span>
                                                    <span className="badge bg-primary">
                                                        <FontAwesomeIcon icon={getCategoryIcon(formData.category)} className="me-1" />
                                                        {formData.category.replace('-', ' ').toUpperCase()}
                                                    </span>
                                                </div>
                                                <p className="text-muted small mb-0">
                                                    {formData.description.length > 100 
                                                        ? `${formData.description.substring(0, 100)}...`
                                                        : formData.description || 'No description provided'
                                                    }
                                                </p>
                                            </div>
                                        ) : (
                                            <p className="text-muted small mb-0">
                                                Fill in the form to see a preview of your ticket
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Help Tips */}
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white border-0">
                                        <h6 className="card-title mb-0">Tips for Better Support</h6>
                                    </div>
                                    <div className="card-body">
                                        <ul className="list-unstyled small">
                                            <li className="mb-2">
                                                <FontAwesomeIcon icon={faInfoCircle} className="text-primary me-2" />
                                                Be specific about the issue
                                            </li>
                                            <li className="mb-2">
                                                <FontAwesomeIcon icon={faInfoCircle} className="text-primary me-2" />
                                                Include error messages if any
                                            </li>
                                            <li className="mb-2">
                                                <FontAwesomeIcon icon={faInfoCircle} className="text-primary me-2" />
                                                Describe steps to reproduce
                                            </li>
                                            <li className="mb-2">
                                                <FontAwesomeIcon icon={faInfoCircle} className="text-primary me-2" />
                                                Attach relevant screenshots
                                            </li>
                                            <li className="mb-0">
                                                <FontAwesomeIcon icon={faInfoCircle} className="text-primary me-2" />
                                                Mention your browser/device
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <DashboardFooter />
        </>
    );
};

export default CreateTicket;
